import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_BASE_URL;

const headers = () => ({
  'Content-Type': 'application/json',
  'token': localStorage.getItem('token') || ''
});

// Cache responses for 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;
const cache: { [key: string]: { data: string[]; timestamp: number } } = {};

export const getPartnersList = async (): Promise<string[]> => {
  const cacheKey = 'partners_names';
  const now = Date.now();

  // Check cache first
  if (cache[cacheKey] && (now - cache[cacheKey].timestamp) < CACHE_DURATION) {
    console.log('Returning cached partner names');
    return cache[cacheKey].data;
  }

  try {
    console.log('Fetching partner names from API');
    const response = await axios.get<{ names: string[] }>(
      `${API_BASE}/adminusers/v1/names/list?type=partner`,
      { 
        headers: headers(),
        baseURL: API_BASE
      }
    );
    
    if (response.data?.names && Array.isArray(response.data.names)) {
      const names = response.data.names
        .filter(name => name && name.trim() !== '')
        .map(name => name.trim());

      // Cache the results
      cache[cacheKey] = {
        data: names,
        timestamp: now
      };
      
      return names;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching partner names:', error);
    throw error; // Throw error to handle in component
  }
};

const formatDateForAPI = (dateStr: string) => {
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error('Date formatting error:', error);
    throw new Error('Invalid date format');
  }
};

export const getSettlements = async (params: {
  partner?: string;
  merchant?: string;
  from?: string;
  to?: string;
  merchantbank?: boolean
  merchantgst?: boolean;
  lender?: string;
}): Promise<Blob> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.partner) queryParams.append('partner', params.partner.trim());
    if (params.merchant) queryParams.append('merchant', params.merchant.trim());
    if (params.from) queryParams.append('from', formatDateForAPI(params.from));
    if (params.to) queryParams.append('to', formatDateForAPI(params.to));
    if (params.merchantbank) queryParams.append('merchantbank', 'true');
    if (params.merchantgst) queryParams.append('merchantgst', 'true');
    if (params.lender) queryParams.append('lender', params.lender.trim());

    console.log('API Request URL:', `${API_BASE}/adminorder/export/settlements?${queryParams.toString()}`);

    const response = await axios.get(
      `${API_BASE}/adminorder/export/settlements?${queryParams.toString()}`,
      { 
        headers: headers(),
        responseType: 'blob',
        timeout: 0,
      }
    );
    
    // Handle JSON error responses
    const contentType = response.headers['content-type'];
    if (contentType && contentType.includes('application/json')) {
      const text = await response.data.text();
      const error = JSON.parse(text);
      throw new Error(error.message || 'Failed to export data');
    }
    
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('No records found for the selected filters');
    }
    console.error('Error fetching settlements:', error);
    throw error;
  }
};

export const getMerchantReport = async (params: {
  partner?: string | string[];
  lender?: string;
  from?: string;
  to?: string;
  reportType?: string;
  bankDetails?: boolean;
  GST?: boolean;
}): Promise<Blob> => {
  try {
    const queryParams = new URLSearchParams();
    
    // Handle partner parameter (could be string or array)
    if (params.partner) {
      if (Array.isArray(params.partner)) {
        // If it's an array, add each partner separately
        params.partner.forEach(partner => {
          queryParams.append('partner', partner.trim());
        });
      } else {
        // If it's a single string
        queryParams.append('partner', params.partner.trim());
      }
    }
    
    if (params.lender) queryParams.append('lender', params.lender.trim());
    if (params.from) queryParams.append('from', formatDateForAPI(params.from));
    if (params.to) queryParams.append('to', formatDateForAPI(params.to));
    if (params.reportType) queryParams.append('reportType', params.reportType);
    if (params.bankDetails) queryParams.append('bankDetails', 'true');
    if (params.GST) queryParams.append('GST', 'true');

    const response = await axios.get(
      `${API_BASE}/report/merchants/download?${queryParams.toString()}`,
      { 
        headers: headers(),
        responseType: 'blob'
      }
    );
    
    // Handle JSON error responses
    const contentType = response.headers['content-type'];
    if (contentType && contentType.includes('application/json')) {
      const text = await response.data.text();
      const error = JSON.parse(text);
      throw new Error(error.message || 'Failed to export data');
    }
    
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('No records found for the selected filters');
    }
    console.error('Error downloading merchant data:', error);
    throw error;
  }
};

export const getPreApprovalReport = async (params: {
  partner?: string;
  merchant?: string;
  lender?: string;
  from?: string;
  to?: string;
}): Promise<Blob> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.partner) queryParams.append('partner', params.partner.trim());
    if (params.merchant) queryParams.append('merchant', params.merchant.trim());
    if (params.lender) queryParams.append('lender', params.lender.trim());
    if (params.from) queryParams.append('from', formatDateForAPI(params.from));
    if (params.to) queryParams.append('to', formatDateForAPI(params.to));

    const response = await axios.get(
      `${API_BASE}/report/preapprovals/download?${queryParams.toString()}`,
      { 
        headers: headers(),
        responseType: 'blob'
      }
    );
    
    // Handle JSON error responses
    const contentType = response.headers['content-type'];
    if (contentType && contentType.includes('application/json')) {
      const text = await response.data.text();
      const error = JSON.parse(text);
      throw new Error(error.message || 'Failed to export data');
    }
    
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('No records found for the selected filters');
    }
    console.error('Error downloading pre-approval data:', error);
    throw error;
  }
};
